import { useState, useEffect, useRef, useCallback } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { searchApplicants, getSearchUsage, getSearchLimits, updateSearchLimits, getSearchStatus, acquireSearchLock, releaseSearchLock } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { 
  Search,
  Phone,
  User,
  Mail,
  Clock,
  Calendar as CalendarIcon,
  FileText,
  Play,
  Star,
  TrendingUp,
  Users,
  AlertCircle,
  Settings,
  ChevronRight,
  Info,
  Timer,
  Lock,
  Loader2,
  CheckCircle,
  XCircle,
  RefreshCw,
  Database,
  Cloud
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const DATE_FILTERS = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'mtd', label: 'Month to Date' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'custom', label: 'Custom Range' },
];

// Timer Component
function SearchTimer({ startTime, maxDuration = 600 }) {
  const [elapsed, setElapsed] = useState(0);
  
  useEffect(() => {
    const start = new Date(startTime).getTime();
    
    const interval = setInterval(() => {
      const now = Date.now();
      const diff = Math.floor((now - start) / 1000);
      setElapsed(diff);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [startTime]);
  
  const remaining = Math.max(0, maxDuration - elapsed);
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  const progress = ((maxDuration - remaining) / maxDuration) * 100;
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">Time remaining</span>
        <span className="font-mono font-bold text-lg text-blue-600">
          {minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
        </span>
      </div>
      <Progress value={progress} className="h-2" />
      <p className="text-xs text-muted-foreground text-center">
        Search will complete within {minutes} minutes
      </p>
    </div>
  );
}

export default function AdminSearchApplicants() {
  // Handle both legacy admin and SaaS tenant contexts
  const outletContext = useOutletContext() || {};
  const adminUrl = outletContext.adminUrl;
  const [phoneNumber, setPhoneNumber] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [isSearching, setIsSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  
  // Search source options
  const [searchLocalCalls, setSearchLocalCalls] = useState(true);  // Default ON
  const [searchCeipal, setSearchCeipal] = useState(false);  // Default OFF
  const [searchUsage, setSearchUsage] = useState({ searches_today: 0, daily_limit: 10, remaining: 10 });
  const [showSettings, setShowSettings] = useState(false);
  const [limits, setLimits] = useState({ user_daily_limit: 3, admin_daily_limit: 10 });
  const [selectedCall, setSelectedCall] = useState(null);
  
  // Search lock state
  const [searchStatus, setSearchStatus] = useState({ is_locked: false, active_search: null });
  const [searchStartTime, setSearchStartTime] = useState(null);
  const searcherId = useRef(`admin_${Date.now()}`);
  const statusPollRef = useRef(null);

  useEffect(() => {
    fetchSearchUsage();
    fetchLimits();
    checkSearchStatus();
    
    // Poll for search status every 5 seconds
    statusPollRef.current = setInterval(checkSearchStatus, 5000);
    
    return () => {
      if (statusPollRef.current) {
        clearInterval(statusPollRef.current);
      }
      // Release lock on unmount if we own it
      releaseSearchLock(searcherId.current).catch(() => {});
    };
  }, []);

  const checkSearchStatus = async () => {
    try {
      const { data } = await getSearchStatus();
      setSearchStatus(data);
      
      // If there's an active search by this user, update UI
      if (data.is_locked && data.active_search?.searcher === searcherId.current) {
        setIsSearching(true);
        if (!searchStartTime) {
          setSearchStartTime(data.active_search.started_at);
        }
      } else if (!data.is_locked && isSearching && searchStartTime) {
        // Search completed
        setIsSearching(false);
      }
    } catch (error) {
      console.error('Failed to check search status:', error);
    }
  };

  const fetchSearchUsage = async () => {
    try {
      const { data } = await getSearchUsage('admin', 'admin');
      setSearchUsage(data);
    } catch (error) {
      console.error('Failed to fetch search usage:', error);
    }
  };

  const fetchLimits = async () => {
    try {
      const { data } = await getSearchLimits();
      setLimits(data);
    } catch (error) {
      console.error('Failed to fetch limits:', error);
    }
  };

  const handleSearch = async () => {
    if (!phoneNumber.trim()) {
      toast.error('Please enter a phone number to search');
      return;
    }

    if (!searchLocalCalls && !searchCeipal) {
      toast.error('Please select at least one search source');
      return;
    }

    if (searchUsage.remaining <= 0) {
      toast.error('Daily search limit reached. Please try again tomorrow.');
      return;
    }

    // Check if another search is in progress (only for CEIPAL searches)
    if (searchCeipal && searchStatus.is_locked && searchStatus.active_search?.searcher !== searcherId.current) {
      toast.error('Another CEIPAL search is in progress. Please wait for it to complete.');
      return;
    }

    // Try to acquire lock only for CEIPAL searches
    if (searchCeipal) {
      try {
        const { data: lockData } = await acquireSearchLock(phoneNumber, searcherId.current);
        
        if (!lockData.acquired) {
          toast.error(lockData.reason || 'Could not start search. Another search is in progress.');
          return;
        }
      } catch (error) {
        toast.error('Failed to start search. Please try again.');
        return;
      }
    }

    setIsSearching(true);
    setSearchResult(null);
    setSearchStartTime(new Date().toISOString());

    // Determine search sources
    const searchSources = searchLocalCalls && searchCeipal ? 'both' : 
                          searchCeipal ? 'ceipal' : 'calls';

    const searchMessage = searchCeipal 
      ? 'Search started. CEIPAL search may take up to 10 minutes for large databases.'
      : 'Searching local calls...';
    
    toast.info(searchMessage, { duration: 3000 });

    try {
      const { data } = await searchApplicants(
        phoneNumber,
        'admin',
        'admin',
        dateFilter === 'all' ? null : dateFilter,
        dateFilter === 'custom' && customDateRange.from ? customDateRange.from.toISOString() : null,
        dateFilter === 'custom' && customDateRange.to ? customDateRange.to.toISOString() : null,
        searchSources
      );
      
      setSearchResult(data);
      setSearchUsage(prev => ({
        ...prev,
        searches_today: prev.searches_today + 1,
        remaining: data.searches_remaining
      }));
      
      if (data.applicant_info) {
        toast.success(`Found applicant: ${data.applicant_info.name || 'Unknown'}`, {
          duration: 5000
        });
      } else if (data.total_calls > 0) {
        toast.success(`Found ${data.total_calls} call(s) for this number`);
      } else {
        toast.info('No results found for this phone number');
      }
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Search failed. Please try again.');
      }
    } finally {
      setIsSearching(false);
      setSearchStartTime(null);
      // Release the lock (only if we acquired one for CEIPAL search)
      if (searchCeipal) {
        await releaseSearchLock(searcherId.current).catch(() => {});
      }
      checkSearchStatus();
    }
  };

  const handleCancelSearch = async () => {
    try {
      await releaseSearchLock(searcherId.current);
      setIsSearching(false);
      setSearchStartTime(null);
      toast.info('Search cancelled');
      checkSearchStatus();
    } catch (error) {
      toast.error('Failed to cancel search');
    }
  };

  const handleUpdateLimits = async () => {
    try {
      await updateSearchLimits(limits.user_daily_limit, limits.admin_daily_limit);
      toast.success('Search limits updated successfully');
      setShowSettings(false);
      fetchSearchUsage();
    } catch (error) {
      toast.error('Failed to update limits');
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Check if system is locked by another user
  const isLockedByOther = searchStatus.is_locked && 
    searchStatus.active_search?.searcher !== searcherId.current;

  return (
    <div className="space-y-6" data-testid="admin-search-applicants">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Search Applicants</h1>
          <p className="text-muted-foreground">Search for applicants by phone number across CEIPAL and local database</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-sm px-3 py-1">
            <Search className="h-3.5 w-3.5 mr-1.5" />
            {searchUsage.remaining} / {searchUsage.daily_limit} searches remaining
          </Badge>
          <Button variant="outline" size="sm" onClick={() => setShowSettings(true)}>
            <Settings className="h-4 w-4 mr-1" />
            Limits
          </Button>
        </div>
      </div>

      {/* System Lock Status Banner */}
      {isLockedByOther && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-amber-100 rounded-full">
                <Lock className="h-5 w-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-amber-800">Search In Progress</h3>
                <p className="text-sm text-amber-700">
                  Another user is searching for: {searchStatus.active_search?.phone_number}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-amber-600">Available in</p>
                <p className="font-mono font-bold text-amber-800">
                  {Math.floor(searchStatus.active_search?.remaining_seconds / 60)}:
                  {(searchStatus.active_search?.remaining_seconds % 60).toString().padStart(2, '0')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search Card */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-blue-500" />
            Phone Number Search
          </CardTitle>
          <CardDescription>
            Enter a phone number to find applicant information and call history
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder="Enter phone number (e.g., 3103508777)"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !isSearching && !isLockedByOther && handleSearch()}
                className="h-12 text-lg"
                disabled={isSearching || isLockedByOther}
              />
            </div>
            <Select value={dateFilter} onValueChange={setDateFilter} disabled={isSearching || isLockedByOther}>
              <SelectTrigger className="w-[180px] h-12">
                <SelectValue placeholder="Time filter" />
              </SelectTrigger>
              <SelectContent>
                {DATE_FILTERS.map((filter) => (
                  <SelectItem key={filter.value} value={filter.value}>
                    {filter.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isSearching ? (
              <Button 
                variant="destructive" 
                className="h-12 px-8"
                onClick={handleCancelSearch}
              >
                <XCircle className="h-5 w-5 mr-2" />
                Cancel
              </Button>
            ) : (
              <Button 
                onClick={handleSearch} 
                disabled={isLockedByOther || searchUsage.remaining <= 0 || (!searchLocalCalls && !searchCeipal)}
                className="h-12 px-8 btn-primary"
              >
                <Search className="h-5 w-5 mr-2" />
                Search
              </Button>
            )}
          </div>

          {/* Search Source Options */}
          <div className="flex items-center gap-6 mt-4 p-4 bg-muted/50 rounded-lg">
            <span className="text-sm font-medium text-slate-700">Search in:</span>
            <div className="flex items-center gap-2">
              <Checkbox
                id="search-calls"
                checked={searchLocalCalls}
                onCheckedChange={setSearchLocalCalls}
                disabled={isSearching}
              />
              <Label htmlFor="search-calls" className="flex items-center gap-2 cursor-pointer">
                <Database className="h-4 w-4 text-green-600" />
                <span>Local Calls</span>
                <Badge variant="outline" className="text-xs bg-green-50 text-green-700">Fast</Badge>
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="search-ceipal"
                checked={searchCeipal}
                onCheckedChange={setSearchCeipal}
                disabled={isSearching}
              />
              <Label htmlFor="search-ceipal" className="flex items-center gap-2 cursor-pointer">
                <Cloud className="h-4 w-4 text-blue-600" />
                <span>CEIPAL API</span>
                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">~10 min</Badge>
              </Label>
            </div>
            {searchCeipal && (
              <p className="text-xs text-amber-600 ml-auto flex items-center gap-1">
                <Info className="h-3 w-3" />
                CEIPAL search searches up to 50,000 records
              </p>
            )}
          </div>

          {dateFilter === 'custom' && (
            <div className="flex gap-4 mt-4">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[200px]">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {customDateRange.from ? format(customDateRange.from, 'PP') : 'Start date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={customDateRange.from}
                    onSelect={(date) => setCustomDateRange(prev => ({ ...prev, from: date }))}
                  />
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[200px]">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {customDateRange.to ? format(customDateRange.to, 'PP') : 'End date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={customDateRange.to}
                    onSelect={(date) => setCustomDateRange(prev => ({ ...prev, to: date }))}
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Search In Progress */}
      {isSearching && searchStartTime && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-6">
            <div className="flex items-start gap-6">
              <div className="p-3 bg-blue-100 rounded-full">
                <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-800 mb-2">Searching External Database...</h3>
                <p className="text-sm text-blue-700 mb-4">
                  Searching for phone number: <strong>{phoneNumber}</strong>
                </p>
                <SearchTimer startTime={searchStartTime} maxDuration={600} />
                <div className="mt-4 p-3 bg-blue-100 rounded-lg">
                  <p className="text-xs text-blue-700 flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    The CEIPAL database contains thousands of records. Results will appear as soon as they are found.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {searchResult && !isSearching && (
        <div className="space-y-6">
          {/* Applicant Info Card */}
          {searchResult.applicant_info ? (
            <Card className="card-elevated border-green-200">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="h-5 w-5" />
                    Applicant Found
                  </CardTitle>
                  <Badge variant="outline" className="text-green-600 border-green-300">
                    Source: {searchResult.applicant_info.source === 'ceipal_api' ? 'CEIPAL API' : 'Local Database'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <User className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Name</p>
                      <p className="font-medium">{searchResult.applicant_info.name || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <Phone className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Phone</p>
                      <p className="font-medium">{searchResult.applicant_info.phone_number || searchResult.phone_number}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <Mail className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="font-medium">{searchResult.applicant_info.email || 'N/A'}</p>
                    </div>
                  </div>
                  {searchResult.applicant_info.location && (
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <Info className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Location</p>
                        <p className="font-medium">
                          {searchResult.applicant_info.location}
                          {searchResult.applicant_info.state && `, ${searchResult.applicant_info.state}`}
                        </p>
                      </div>
                    </div>
                  )}
                  {searchResult.applicant_info.work_authorization && (
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Work Authorization</p>
                        <p className="font-medium">{searchResult.applicant_info.work_authorization}</p>
                      </div>
                    </div>
                  )}
                  {searchResult.applicant_info.skills && (
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg col-span-full">
                      <TrendingUp className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Skills</p>
                        <p className="font-medium text-sm">{searchResult.applicant_info.skills}</p>
                      </div>
                    </div>
                  )}
                </div>
                
                {searchResult.applicant_info.search_method && (
                  <p className="text-xs text-muted-foreground mt-4">
                    Found via: {searchResult.applicant_info.search_method}
                    {searchResult.applicant_info.page_found && ` (Page ${searchResult.applicant_info.page_found})`}
                    {searchResult.applicant_info.total_records_checked && ` - ${searchResult.applicant_info.total_records_checked} records checked`}
                  </p>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-800">No Applicant Found in CEIPAL</p>
                    <p className="text-sm text-amber-700">
                      {searchResult.ceipal_api_error || 'The phone number was not found in the CEIPAL database.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Call History */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="h-5 w-5 text-blue-500" />
                Call History
                <Badge variant="secondary" className="ml-2">
                  {searchResult.total_calls || 0} calls
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {searchResult.calls && searchResult.calls.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Agent</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {searchResult.calls.map((call) => (
                      <TableRow key={call.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{new Date(call.call_date).toLocaleDateString()}</p>
                            <p className="text-xs text-muted-foreground">{new Date(call.call_date).toLocaleTimeString()}</p>
                          </div>
                        </TableCell>
                        <TableCell>{formatDuration(call.duration || 0)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-6 w-6">
                              <AvatarFallback className="text-xs">
                                {call.user_name?.charAt(0) || '?'}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm">{call.user_name || 'Unknown'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            (call.overall_score || 0) >= 8 ? 'bg-green-100 text-green-700' :
                            (call.overall_score || 0) >= 6 ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }>
                            {call.overall_score?.toFixed(1) || 'N/A'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedCall(call)}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Phone className="h-12 w-12 mx-auto text-muted-foreground/70 mb-3" />
                  <p>No calls found for this phone number</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Search Limits Configuration</DialogTitle>
            <DialogDescription>
              Configure daily search limits for users and admins
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">User Daily Limit</label>
              <Input
                type="number"
                value={limits.user_daily_limit}
                onChange={(e) => setLimits(prev => ({ ...prev, user_daily_limit: parseInt(e.target.value) || 0 }))}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">Maximum searches per day for regular users</p>
            </div>
            <div>
              <label className="text-sm font-medium">Admin Daily Limit</label>
              <Input
                type="number"
                value={limits.admin_daily_limit}
                onChange={(e) => setLimits(prev => ({ ...prev, admin_daily_limit: parseInt(e.target.value) || 0 }))}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">Maximum searches per day for administrators</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
            <Button onClick={handleUpdateLimits} className="btn-primary">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Call Details Dialog */}
      <Dialog open={!!selectedCall} onOpenChange={() => setSelectedCall(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Call Details</DialogTitle>
          </DialogHeader>
          {selectedCall && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Date & Time</p>
                  <p className="font-medium">
                    {new Date(selectedCall.call_date).toLocaleString()}
                  </p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Duration</p>
                  <p className="font-medium">{formatDuration(selectedCall.duration || 0)}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Agent</p>
                  <p className="font-medium">{selectedCall.user_name || 'Unknown'}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Overall Score</p>
                  <p className="font-medium">{selectedCall.overall_score?.toFixed(1) || 'N/A'} / 10</p>
                </div>
              </div>
              
              {selectedCall.audio_url && (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-2">Audio Recording</p>
                  <audio controls className="w-full">
                    <source src={selectedCall.audio_url} type="audio/mpeg" />
                  </audio>
                </div>
              )}
              
              {selectedCall.summary && (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-2">Call Summary</p>
                  <p className="text-sm">{selectedCall.summary}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
